import React, { useRef } from "react"
import type { Item } from "./Nestable"
import "./NestableItem.scoped.scss"
import { useDispatch } from "react-redux";
import { updateCreateRcStepCollapsed, updateStepCollapsed } from "store/manageSW/manageSWActions";
import dragIcon from "media/icons/dls/drag.svg";

type DragState = {
    path: number[];
    position: "before" | "after" | "inside";
    height?: number;
}

type Props = {
    item: Item;
    depth: number;
    path: number[];
    renderItem: any;
    childrenProps: string;
    onDragStart: (data: { item: Item; path: number[], height: number }) => void;
    onDragEnd: () => void;
    onDrop: (targetPath: number[], position: DragState["position"]) => void;
    onToggle: (path: number[]) => void;
    dragState: DragState | null;
    setDragState: (state: DragState | null) => void;
    renderCollapseIcon: any;
    displayAll: boolean;
    isStepList?: boolean;
}

export const NestableItem: React.FC<Props> = ({
    item,
    depth,
    path,
    renderItem,
    childrenProps,
    onDragStart,
    onDrop,
    onToggle,
    dragState,
    setDragState,
    renderCollapseIcon,
    onDragEnd,
    displayAll,
    isStepList
}) => {
    const dispatch = useDispatch();
    const hasChildren = item[childrenProps] && item[childrenProps].length > 0;
    const itemRef = useRef<HTMLLIElement>(null);

    const isBeforeActive = dragState?.position === "before" && dragState.path.join(",") === path.join(",");

    const isAfterActive = dragState?.position === "after" && dragState.path.join(",") === path.join(",");

    const isInsideActive = dragState?.position === "inside" && dragState.path.join(",") === path.join(",");

    const activeHeight = dragState?.height ?? 0;

    const handleDragStart = () => {
        const height = itemRef.current?.getBoundingClientRect().height || 0;

        onDragStart({ item, path, height })
    }

    const onDragOverBefore = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        setDragState({ path, position: "before", height: activeHeight })
    }

    const onDropBefore = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        onDrop(path, "before");
    }

    const onDragOverInside = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        setDragState({ path, position: "inside", height: activeHeight })
    }

    const onDropInside = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        onDrop(path, "inside");
    }

    const onDragOverAfter = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        setDragState({ path, position: "after", height: activeHeight })
    }

    const onDropAfter = (e: React.DragEvent) => {
        e.preventDefault();

        if (!dragState) return;

        onDrop(path, "after");
    }

    return (
        <li ref={itemRef} className="nestable-item">
            <div className={`nestable-dropzone before ${isBeforeActive ? "active" : ""}`}
                style={{ height: isBeforeActive ? activeHeight : undefined }}
                onDragOver={onDragOverBefore}
                onDrop={onDropBefore}
            >
                {isBeforeActive && <div className="nestable-drop-indicator" />}
            </div>

            <div className="nestable-item-content" draggable
                onDragStart={handleDragStart}
                onDragEnd={onDragEnd}
                onDragOver={onDragOverInside}
                onDrop={onDropInside}>
                {isInsideActive && <div className="nestable-inside-indicator" />}
                {renderItem({
                    item,
                    depth,
                    collapseIcon:
                        item[childrenProps]?.length ? (
                            <span className="nestable-toggle"
                                onClick={e => {
                                    e.stopPropagation();
                                    onToggle(path)
                                    if(isStepList){
                                        dispatch(updateStepCollapsed({ stepId: item.id.length > 3 ? item.id : Number(item.id), collapsed: !item.collapsed }))
                                    } else {
                                        dispatch(updateCreateRcStepCollapsed({ stepId: item.id.length > 3 ? item.id : Number(item.id), collapsed: !item.collapsed }))
                                    }
                                    
                                }}
                            >
                                {renderCollapseIcon({ isCollapsed: item.collapsed, id: item.id, parentGuid: item.parentGuid, sortOrder: item.sortOrder, stepId: item.stepId })}
                            </span>
                        ) : (
                            <img
                                className="icon-small"
                                alt="drag"
                                src={dragIcon}
                            />
                        )

                })}
            </div>

            {(item.collapsed || displayAll) && hasChildren && (
                <ul className="nestable-list">
                    {item[childrenProps].map((child: Item, index: number) => (
                        <NestableItem
                            key={child.id}
                            item={child}
                            depth={depth + 1}
                            path={[...path, index]}
                            renderItem={renderItem}
                            childrenProps={childrenProps}
                            onDragStart={onDragStart}
                            onDrop={onDrop}
                            onToggle={onToggle}
                            dragState={dragState}
                            setDragState={setDragState}
                            renderCollapseIcon={renderCollapseIcon}
                            onDragEnd={onDragEnd}
                            displayAll={displayAll}
                        />
                    ))}
                </ul>
            )}
            <div className={`nestable-dropzone after ${isAfterActive ? "active" : ""}`}
                style={{ height: isAfterActive ? activeHeight : undefined }}
                onDragOver={onDragOverAfter}
                onDrop={onDropAfter}>
                {isAfterActive && <div className="nestable-drop-indicator" />}
            </div>
        </li>
    )
}